<script lang="ts" setup>
import type { DetailsWheels, ElementsParameters } from '@autobid/ui/types/components/CarDetails'
import { useDetailsWheels } from '@autobid/ui/composables/car/useDetailsWheels'
import { computed } from 'vue'

interface Props {
  title: string
  wheels?: DetailsWheels
}

const props = defineProps<Props>()

const shouldBeRendered = computed(() => {
  const { list, images, tables } = props.wheels ?? {}

  return list?.length || images?.length || tables?.find(el => el.table.tableCells?.length)
})

const formattedList = computed(() => {
  if (!props.wheels?.list?.length) {
    return []
  }

  // parse values [string, string][] to the format that is accepted by the SectionsDetailsElementsParameters component
  return props.wheels.list.reduce((acc: ElementsParameters, element) => {
    const [label, value] = element

    if (Array.isArray(value)) {
      const joinedValues = value.map(el => el.join(': '))

      acc.push([label, joinedValues])
      return acc
    }

    acc.push([label, value])
    return acc
  }, [])
})

const { tableWithWheelTitles } = useDetailsWheels(props.wheels)
</script>

<template>
  <tr v-if="shouldBeRendered">
    <td>
      <table class="w-full border-collapse">
        <thead>
          <SectionsDetailsPdfElementsHeading as="th">
            {{ title }}
          </SectionsDetailsPdfElementsHeading>
        </thead>
        <tbody>
          <tr>
            <td>
              <SectionsDetailsElementsParameters
                v-if="formattedList.length"
                :parameters="formattedList"
              />

              <SectionsDetailsPdfElementsImages
                v-if="wheels?.images?.length"
                :images="wheels.images"
              />

              <template
                v-for="table in tableWithWheelTitles"
                :key="table.title"
              >
                <table
                  v-if="table.table.tableCells.length"
                  class="mt-3 w-full border-collapse"
                >
                  <thead>
                    <tr class="break-inside-avoid">
                      <th
                        colspan="10"
                        style="background-color: #e6e6e6"
                        class="px-2 py-1 text-left font-normal uppercase"
                      >
                        {{ table.title }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <!-- margin -->
                      <td colspan="10" style="height: 5px" />
                    </tr>

                    <SectionsDetailsElementsTable :table="table.table" />
                  </tbody>
                </table>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>
</template>
